import { ArrowRight, CalendarDays } from 'lucide-react'
import { Button } from '@insightai/ui'
import { Suspense, lazy } from 'react'
import { Link } from 'react-router-dom'

/**
 * M00-04 — the animated TOSCA MECE decomposition preview.
 *
 * §8 is explicit: reuse a read-only IssueTreeGraph, do not build a bespoke
 * marketing animation, "or the two will diverge". So the hero card renders the
 * same component the Brief Builder will use, in `readOnly` mode.
 *
 * It is lazily imported because that component carries d3-hierarchy/shape, and
 * the marketing entry chunk has its own budget (M00-12). The fallback is a sized
 * placeholder so the hero does not reflow when the chunk lands — layout shift in
 * the hero is the fastest way to fail a Lighthouse CLS check.
 */
const ToscaPreviewCard = lazy(() => import('./ToscaPreviewCard'))

export function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-border-subtle">
      {/* Decorative wash. aria-hidden and pointer-events-none so it cannot
          intercept clicks or be announced. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(60rem 30rem at 15% -10%, var(--surface-selected), transparent 60%)',
        }}
      />

      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:items-center lg:py-24">
        <div className="flex flex-col gap-6">
          <p className="inline-flex w-fit items-center gap-2 rounded-full border border-border-subtle bg-surface-raised px-3 py-1 text-caption text-text-secondary">
            <span aria-hidden className="size-1.5 rounded-full bg-state-verified" />
            Every claim traces to a retrievable source
          </p>

          <h1 className="text-display-lg text-balance">
            Research-grade answers,
            <br />
            <span className="text-brand-primary">without the six-week wait</span>
          </h1>

          <p className="max-w-xl text-body text-text-secondary">
            Describe your business question in plain language. InsightAI structures it with TOSCA,
            decomposes it into a MECE issue tree, runs the research, and returns findings where every
            claim carries its source, its confidence and its method.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Button size="lg" asChild iconEnd={<ArrowRight aria-hidden className="size-4" />}>
              <Link to="/auth/signup">Start Free Brief</Link>
            </Button>
            <Button
              size="lg"
              variant="secondary"
              asChild
              iconStart={<CalendarDays aria-hidden className="size-4" />}
            >
              <Link to="/resources#demo">Book Platform Demo</Link>
            </Button>
          </div>

          <dl className="grid grid-cols-3 gap-4 pt-2">
            {[
              { value: '10 min', label: 'From question to submitted brief' },
              { value: '4–12 h', label: 'Desk research turnaround' },
              { value: '100%', label: 'Claims with cited provenance' },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col gap-0.5">
                <dt className="text-h2 tabular-nums text-text-primary">{stat.value}</dt>
                <dd className="text-caption text-text-secondary">{stat.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        <Suspense
          fallback={
            <div
              className="h-[420px] w-full animate-pulse rounded-lg border border-border-subtle bg-surface-raised"
              aria-hidden
            />
          }
        >
          <ToscaPreviewCard />
        </Suspense>
      </div>
    </section>
  )
}
